/**
 * High moisture alert (user, 2026-10-27): a Moisture Meter Reading on an arrival sample above the
 * crop's Normal moisture standard (QC Standards, `crops.max_moisture_pct`; Ash Gourd ≤ 7%, so 8% is
 * high) alerts the Arrival Manager the moment the analyst saves it. The web shows it three ways:
 * the top strip's bell (`qc.moisture_alert`), a "Moisture high" item on the live Arrival dashboard,
 * and Reports > High Moisture Lots, which lists each lot until it is dried.
 */
import type {
  ReportCellFormat,
  ReportColumn,
  ReportDef,
} from "./warehouse-reports";

/**
 * Where a high reading came from: the QC lab's Moisture Meter Reading, or the S-Capture feed's
 * moisture % (0159, user 2026-10-27), raised at the gate pass or when the lot joins an arrival.
 */
export type MoistureAlertSource = "qc_meter" | "scapture";
export const MOISTURE_SOURCE_LABEL: Record<MoistureAlertSource, string> = {
  qc_meter: "QC meter",
  scapture: "S-Capture",
};

/** One unseen alert, as the bell lists it. */
export interface MoistureAlert {
  id: number;
  facilityId: number;
  source: MoistureAlertSource;
  plant: string;
  /** The QC sample; null on an S-Capture alert. */
  sampleNo: string | null;
  /** The S-Capture dispatch an S-Capture alert's lot is on. */
  dispatchId: string | null;
  lotNumber: string | null;
  crop: string | null;
  variety: string | null;
  /** The meter mean, and the crop's Normal maximum it was judged against. */
  readingPct: number;
  stdMaxPct: number;
  documentId: number | null;
  docNo: string | null;
  formCode: string | null;
  analyst: string | null;
  raisedAt: string;
}

/** Where a high-moisture lot stands on the report; a dried lot leaves the report. */
export const HIGH_MOISTURE_STATUS = {
  awaiting: "Awaiting drying",
  in_dryer: "In dryer",
  partly_dried: "Partly dried",
} as const;
export type HighMoistureStatus = keyof typeof HIGH_MOISTURE_STATUS;

/**
 * One lot still to dry, on its truck. Both readings are shown whichever of them was high (user,
 * 2026-10-27): the S-Capture feed's % and the lab's latest Moisture Meter Reading (null = not tested).
 */
export interface HighMoistureLot {
  alertId: number;
  cropId: number | null;
  crop: string | null;
  variety: string | null;
  lotNumber: string | null;
  sampleNo: string | null;
  bags: number;
  qty: number;
  scapturePct: number | null;
  qcPct: number | null;
  stdMaxPct: number;
  scaptureHigh: boolean;
  qcHigh: boolean;
  status: HighMoistureStatus;
  recordedAt: string;
  analyst: string | null;
}

/** A truck let in at the gate with lots read high and not yet dried: the arrival, else the dispatch. */
export interface HighMoistureTruck {
  key: string;
  facilityId: number;
  plant: string;
  documentId: number | null;
  docNo: string | null;
  formCode: string | null;
  dispatchId: string | null;
  arrivalDate: string | null;
  vehicle: string | null;
  gatePassNo: string | null;
  gateInAt: string;
  lots: HighMoistureLot[];
}

/** The lot's highest reading against its standard, and where it came from. */
export function worstReading(l: HighMoistureLot): {
  pct: number;
  source: MoistureAlertSource;
} {
  if (l.qcPct !== null && (l.scapturePct === null || l.qcPct >= l.scapturePct))
    return { pct: l.qcPct, source: "qc_meter" };
  return { pct: l.scapturePct ?? l.qcPct ?? 0, source: "scapture" };
}

/** "11.4% (std ≤ 10%)": the bell's and the dashboard's words for a reading. */
export const moistureAlertText = (readingPct: number, stdMaxPct: number) =>
  `${readingPct}% (std ≤ ${stdMaxPct}%)`;

export type HighMoistureReportKey = "high-moisture";

const c = (
  key: string,
  label: string,
  format: ReportCellFormat,
  more: Partial<Omit<ReportColumn, "key" | "label" | "format">> = {}
): ReportColumn => ({
  key,
  label,
  format,
  align:
    format === "int" || format === "qty" || format === "pct"
      ? "right"
      : format === "text"
        ? "left"
        : "center",
  ...more,
});

export const HIGH_MOISTURE_REPORTS: {
  [K in HighMoistureReportKey]: ReportDef & { key: K };
} = {
  "high-moisture": {
    key: "high-moisture",
    menu: "High Moisture Lots",
    title: "High Moisture Lots",
    asOn: true,
    kind: "sectioned",
    filters: ["crop"],
    sectionLabels: ["Arrival", "Vehicle"],
    columns: [
      c("serial", "#", "int", { align: "center" }),
      c("lotNumber", "Lot No.", "text"),
      c("crop", "Crop", "text"),
      c("variety", "Variety", "text"),
      c("bags", "Bags", "int", { total: true }),
      c("qty", "Qty (kg)", "qty", { total: true }),
      c("scapturePct", "S-Capture %", "text", { align: "right" }),
      c("qcPct", "QC test %", "text", { align: "right" }),
      c("stdMaxPct", "Standard ≤ %", "pct"),
      c("status", "Status", "text"),
      c("recordedAt", "Recorded at", "text"),
    ],
    tooMany: "Too many lots. Choose a crop.",
    note: "Trucks let in at the gate (gate pass in) with arrival lots whose S-Capture moisture % or QC Moisture Meter Reading (QC test %) was above the crop's Normal moisture standard (QC Standards). Both readings are shown; QC test % is the lab's latest meter reading, Not tested until there is one. A lot leaves the report once it is dried: a submitted Drying Slip of the whole lot, or its drying ended in the cob dryer, on the lot or a lot made from it. Partly dried is a Drying Slip of part of the lot; the rest is still to dry.",
  },
};
